// Vendored from the host app (conek-rails
// `app/frontend/shared/lib/notifications/notification-deep-link.ts`) so the gem
// has no `shared/*` host-alias coupling. The host version resolves links through the
// generated js-routes `spaceRoutes`; this port uses the gem's minimal
// `../space-routes.js` shims, which reproduce the same paths exactly.
//
// Maps a Noticed notifier type to the space-scoped deep-link a notification row
// should navigate to (CONEK-4920). Returns `null` when the notification has no
// navigation target (deletions, system alerts, or an id-less row) — the row
// renders as plain, non-clickable text, mirroring the React `GenericWrapper`.
//
// Keep in sync with the server-side icon `<template>` dictionary. Every
// SupportTicket and SupportCsat notifier must appear here (CONEK-4745) or it
// degrades to a no-link row.

import {
  calendarPath,
  companyPath,
  contactPath,
  contactsPath,
  opportunityPath,
  previewPdfQuotePath,
  quotePath,
  taskPath,
} from "../space-routes"

// Support-ticket links are built from the current URL's space prefix rather than
// a route helper (the tickets routes live outside the generated space helpers) —
// verbatim port of getSupportTicketPath in notification-wrappers.tsx.
function supportTicketPath(id) {
  const match = /^(\/s:[^/]+)/.exec(window.location.pathname)
  const spacePrefix = match ? match[1] : ""
  return `${spacePrefix}/support/tickets/${id}`
}

// A strategy resolves (type, id) -> href, or null when it can't produce a link.
// The vendored space-routes return null when there is no space context or no id,
// so the null-propagation behaviour of the host's idLink/staticLink helpers is
// preserved by the route functions themselves.
const taskLink = (id) => {
  const href = taskPath(id)
  return id && href ? { href, download: false } : null
}
const opportunityLink = (id) => {
  const href = opportunityPath(id)
  return id && href ? { href, download: false } : null
}
const quoteLink = (id) => {
  const href = quotePath(id)
  return id && href ? { href, download: false } : null
}
const pdfPreviewLink = (id) => {
  const href = previewPdfQuotePath(id)
  return id && href ? { href, download: false } : null
}
const contactLink = (id) => {
  const href = contactPath(id)
  return id && href ? { href, download: false } : null
}
const companyLink = (id) => {
  const href = companyPath(id)
  return id && href ? { href, download: false } : null
}
const supportTicketLink = (id) =>
  id ? { href: supportTicketPath(id), download: false } : null
const messageLink = () => {
  const href = contactsPath()
  return href ? { href, download: false } : null
}
const bookingLink = () => {
  const href = calendarPath()
  return href ? { href, download: false } : null
}

// The id IS the download URL (item exports). No id -> no link.
const downloadLink = (id) => (id ? { href: id, download: true } : null)

// CONEK-7953 — notifier types whose destination arrives ready-made in `url`,
// resolved SERVER-side behind a permission gate (the host's IotDeviceDeepLink,
// CONEK-7635): the leg, the device page, or nothing at all. For these types
// `url` is authoritative and its ABSENCE means "no link" — never a fallback to
// `recordId`, which is a bare device UUID and would render as a broken href.
// Mirrors the host's `URL_DEEP_LINK_TYPES` (notification-helpers.ts); the host
// parity test fails if a notifier including IotDeviceDeepLink is missing here.
export const URL_DEEP_LINK_TYPES = new Set([
  "IotAlertTriggeredNotifier",
  "IotSensorSilentNotifier",
  "IotSensorBatteryLowNotifier",
  "IotGatewayOfflineNotifier",
  "IotSensorInstalledNotifier",
  "IotSensorImpactNotifier",
])

/** Whether a notifier type carries its deep link in `url` (see above). */
export function isUrlDeepLinkType(type) {
  return URL_DEEP_LINK_TYPES.has(type)
}

// Only a same-origin absolute path is followed; a protocol-relative value or a
// scheme-bearing one is not a destination the server ever builds. The origin is
// checked by parsing the value the way `location.assign` will, not by matching
// characters: the URL parser drops tabs and newlines and reads `\` as `/`, so
// `/<TAB>/evil.example` and `/\evil.example` both resolve to another host.
const serverUrlLink = (url) => {
  if (typeof url !== "string" || !url.startsWith("/")) return null
  let resolved
  try {
    resolved = new URL(url, window.location.origin)
  } catch {
    return null
  }
  return resolved.origin === window.location.origin ? { href: url, download: false } : null
}

const STRATEGIES = {
  // Legacy snake_case types (from stub data)
  task_assigned: taskLink,
  opportunity_updated: opportunityLink,
  order_status_changed: quoteLink,

  // Tasks
  TaskAssignedNotifier: taskLink,
  TaskCommentNotifier: taskLink,
  TaskOverdueNotifier: taskLink,

  // Quotes
  QuoteApprovalRequestNotifier: quoteLink,
  QuoteApprovedNotifier: quoteLink,
  QuoteDisapprovedNotifier: quoteLink,
  QuoteConvertedNotifier: quoteLink,
  QuoteRejectedNotifier: quoteLink,
  QuoteExpiringNotifier: quoteLink,
  QuoteToBePresentedNotifier: quoteLink,
  QuoteCreatedNotifier: quoteLink,
  QuoteAssignedNotifier: quoteLink,
  // CONEK-6991: custom-item quote hand-offs (PM Catalog Setup, Ready to WON, Send back)
  QuotePmCatalogSetupNotifier: quoteLink,
  QuoteReadyToWonNotifier: quoteLink,
  QuoteSentBackNotifier: quoteLink,

  // Orders
  OrderAssigneeChangedNotifier: quoteLink,
  SalesOrderStatusNotifier: quoteLink,
  OrderCommentAddedNotifier: quoteLink,

  // Pipeline / Opportunities
  OpportunityCreatedNotifier: opportunityLink,
  OpportunityUpdatedNotifier: opportunityLink,
  OpportunityStageChangedNotifier: opportunityLink,
  OpportunityWonNotifier: opportunityLink,
  OpportunityAssignedNotifier: opportunityLink,

  // PDF
  PdfGenerationCompleteNotifier: pdfPreviewLink,

  // Collaboration
  MessageReceivedNotifier: messageLink,
  MessageReplyReceivedNotifier: messageLink,
  BookingReminderNotifier: bookingLink,
  BookingCancelledNotifier: bookingLink,
  ContactEventReminderNotifier: contactLink,

  // Contacts (persons)
  ContactAssignedNotifier: contactLink,
  ContactPersonCreatedNotifier: contactLink,
  ContactPersonUpdatedNotifier: contactLink,

  // Contacts (companies)
  ContactCompanyCreatedNotifier: companyLink,
  ContactCompanyUpdatedNotifier: companyLink,

  // Fulfillment
  DeliverableShippedNotifier: quoteLink,
  DeliverableDelayedNotifier: quoteLink,

  // Catalog
  ItemExportReadyNotifier: downloadLink,

  // Support tickets — CONEK-4745 (keep in sync with the icon dictionary).
  SupportTicketAssignedNotifier: supportTicketLink,
  SupportTicketCommentNotifier: supportTicketLink,
  SupportTicketStatusChangedNotifier: supportTicketLink,
  SupportTicketSlaWarningNotifier: supportTicketLink,
  SupportTicketNeglectedNotifier: supportTicketLink,
  SupportTicketMergedNotifier: supportTicketLink,
  SupportTicketAIReviewNotifier: supportTicketLink,
  SupportCsatRequestNotifier: supportTicketLink,

  // IoT — CONEK-7953: the id passed in is the server-resolved `url` (see
  // `deepLinkInputFor`), never the record id.
  ...Object.fromEntries(
    [...URL_DEEP_LINK_TYPES].map((type) => [type, serverUrlLink]),
  ),
}

/**
 * The value `deepLinkFor` resolves for a notification: `url` for the types
 * whose destination is server-resolved (absent `url` → no link), the record id
 * (falling back to `url`) for every other type.
 */
export function deepLinkInputFor(type, item) {
  if (isUrlDeepLinkType(type)) return item.url ?? null
  return item.recordId ?? item.url ?? null
}

/**
 * Resolves the navigation target for a notification. `null` means "no link"
 * (deletions, system alerts, api-key/stock notifiers, or a missing id).
 */
export function deepLinkFor(type, id) {
  const strategy = STRATEGIES[type]
  return strategy ? strategy(id) : null
}
